import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FaPlus, FaChevronRight } from "react-icons/fa";
import HomeShell from "../components/HomeShell";
import { api } from "../api/api";
// `canAccess` è il metro dei ruoli del frontend (auth/access.js: grafia +
// vocabolario, MANAGER/TD → COACH, CHILD → PLAYER, `role` più i contexts
// ACTIVE): lo usa `eIscrivibileAllaClinic`, qui sotto, che dal 15/9 offre
// le persone PER RUOLO — PLAYER e COACH — e non più per età.
import { canAccess } from "../auth/access";
// L'intestazione, l'esecutore dei gesti e le pastiglie sono gli stessi della
// pagina di un evento (Fetta 3, P1): vivono in clinicShared.jsx.
import { IntestazionePlancia, PastiglieEvento, useGestoClinic } from "./clinicShared";
// I pulsanti e i campi sono quelli di sistema (doc 07 §5.1 oro, §5.3 fantasma,
// §5.5 campi): il foglio che li definisce si importa come fanno le altre pagine
// che li usano (SeasonManagement.jsx, TeamAssignment.jsx). Nessuno stile
// nuovo per ciò che esiste già.
import "../components/AdminTools.css";
import "./ClinicEvents.css";

/**
 * Clinic / Off Season / Specials — la plancia dell'amministrazione.
 * Cantiere CLINICS, Fetta B (15/9/2026). Base di verità: DISEGNO v2 §3, §9.
 *
 * ⚙️ DALLA FETTA 3 «PLANCIA A PAGINE» (23/9/2026) LA PLANCIA È DUE PAGINE.
 * Qui, su /clinic-events, restano «New event» e l'ELENCO degli eventi (con
 * «Show hidden events»); un clic su una riga apre /clinic-events/:id,
 * ClinicEventDetail.jsx, che contiene tutto ciò che fino ad allora si apriva a
 * tendina sotto la riga — leva apri/chiudi, Hide/Unhide, link pubblico,
 * modifica, gruppi con le sessioni, iscritti — col comportamento di prima.
 * Il cappello di quella pagina dice il resto.
 *
 * Da qui un ADMIN o un SUPERADMIN crea un evento (nome e descrizione) → nasce
 * OPEN, e ne vede l'elenco.
 *
 * ⚠️ LE DATE DELL'EVENTO NON CI SONO PIÙ (Fetta 3): né nel modulo «New event»
 * né nelle righe dell'elenco. Le date vere stanno nelle SESSIONI dei gruppi
 * (Calendario clinic, Fetta 1), giorni e orari in parole nella descrizione. Il
 * server fa nascere l'evento con un elenco di date vuoto.
 *
 * ⚠️ IL CONFINE. Un evento clinic non ha stagione, non ha squadra, non tocca
 * tessere: le clinic sono cross-stagione, l'app principale no (DISEGNO §1). Per
 * questo la pagina non monta la barra della stagione e non nomina annate — non
 * è una svista, è la riga che separa i due mondi.
 *
 * ⚠️ NESSUNA SCRITTURA DAL CLIENT. Ogni gesto passa dalle rotte
 * `/clinic-events*` del backend (gate ADMIN/SUPERADMIN): la collezione
 * `clinicEvents` non ha regola Firestore propria e ricade nel default-deny, che
 * dal browser la rende irraggiungibile. Il gate su questa rotta è cortesia
 * grafica, come ovunque nell'app; la protezione vera è sul server.
 *
 * Ci si arriva dal pulsante «Clinic / Off Season / Specials» delle home
 * dell'amministrazione (HomeAdmin.jsx, HomeSuperadmin.jsx).
 *
 * Regole di identità grafica: doc 07 (palette, pulsanti §5, telaio §7, card
 * §8). Testi a schermo in inglese (CLAUDE.md §3.7).
 */

// CHI SI PUÒ ISCRIVERE DALLE PORTE INTERNE (decisione del committente, 15/9):
// per RUOLO, non per età — PLAYER e COACH, adulti compresi; non PARENT, non
// ADMIN/SUPERADMIN come persone da iscrivere. È la copia a schermo di
// `CLINIC_RUOLI_ISCRIVIBILI` sul server (che decide davvero, sul documento
// riletto): qui serve solo a non offrire un candidato che il server
// rifiuterebbe. Il metro è `canAccess`, cioè il vocabolario di access.js:
// «Manager» e «TD» sono COACH, «Child» è PLAYER, e contano anche i contexts
// ACTIVE. Esportata perché l'area del socio (ClinicMember.jsx) usa lo stesso
// filtro, e dalla Fetta 3 la usa la pagina dell'evento (ClinicEventDetail.jsx,
// l'aggiunta a mano): una sede sola nel frontend.
const RUOLI_ISCRIVIBILI = ["PLAYER", "COACH"];

export function eIscrivibileAllaClinic(persona) {
  return RUOLI_ISCRIVIBILI.some((ruolo) => canAccess(persona, ruolo));
}

// «SHOW HIDDEN EVENTS» RESTA COM'ERA (P1, 24/9). Aperto un evento dall'elenco,
// il Back della sua pagina (fisso su /clinic-events) o l'«indietro» del
// browser rimontano questa pagina: senza memoria l'interruttore tornava spento
// e l'evento nascosto appena guardato spariva dall'elenco. Lo si conserva in
// `sessionStorage`, cioè per la sola sessione del browser: una sessione nuova
// parte spenta, com'era. È una preferenza di vista, nessun dato di persona.
// Se lo storage è negato (finestra privata, dati del sito bloccati) si ripiega
// su «spento» e l'interruttore funziona lo stesso, senza memoria.
const CHIAVE_MOSTRA_NASCOSTI = "qpscClinicEventsShowHidden";

function leggiMostraNascosti() {
  try {
    return window.sessionStorage.getItem(CHIAVE_MOSTRA_NASCOSTI) === "1";
  } catch (err) {
    return false;
  }
}

function scriviMostraNascosti(attivo) {
  try {
    if (attivo) {
      window.sessionStorage.setItem(CHIAVE_MOSTRA_NASCOSTI, "1");
    } else {
      window.sessionStorage.removeItem(CHIAVE_MOSTRA_NASCOSTI);
    }
  } catch (err) {
    // Nessuna memoria: vedi sopra.
  }
}

export default function ClinicEvents() {
  const navigate = useNavigate();

  const [eventi, setEventi] = useState([]);
  const [caricamento, setCaricamento] = useState(true);
  const [erroreCaricamento, setErroreCaricamento] = useState(null);

  // «Show hidden events» (15/9): di default il server manda i soli eventi in
  // vista; con l'interruttore acceso si chiedono anche i nascosti, che
  // arrivano contrassegnati (`hidden: true`). È una lettura, non un filtro
  // locale: l'elenco di default non contiene i nascosti, e non li nasconde.
  // Dalla P1 (24/9) lo stato sopravvive all'uscita dalla pagina per tutta la
  // sessione del browser (vedi `leggiMostraNascosti` in testa al file).
  const [mostraNascosti, setMostraNascosti] = useState(leggiMostraNascosti);

  useEffect(() => {
    scriviMostraNascosti(mostraNascosti);
  }, [mostraNascosti]);

  const { azioneInCorso, erroreAzione, setErroreAzione, esegui } = useGestoClinic();

  // Il form di creazione.
  const [nuovoTitolo, setNuovoTitolo] = useState("");
  const [nuovaDescrizione, setNuovaDescrizione] = useState("");

  const carica = useCallback(async () => {
    setCaricamento(true);
    setErroreCaricamento(null);

    try {
      const elenco = await api.get(
        mostraNascosti ? "/clinic-events?includeHidden=1" : "/clinic-events"
      );
      setEventi(Array.isArray(elenco) ? elenco : []);
    } catch (err) {
      // Il caricamento fallito si dice e basta: qui non c'è nulla da salvare,
      // quindi il messaggio è di sola lettura e non parla di dati perduti.
      setErroreCaricamento(
        err && err.status === 403
          ? "You do not have access to clinic events."
          : "Clinic events could not be loaded. Reload the page; if it keeps failing, the server may be unreachable."
      );
      setEventi([]);
    } finally {
      setCaricamento(false);
    }
  }, [mostraNascosti]);

  useEffect(() => {
    carica();
  }, [carica]);

  // --- CREAZIONE --------------------------------------------------------

  const creaEvento = async () => {
    // Il vaglio vero è del server (è lui che riceve anche ciò che non passa da
    // questa schermata): qui si evita solo di far partire una chiamata che si
    // sa già rifiutata, e si dice perché.
    if (!nuovoTitolo.trim()) {
      setErroreAzione("Event title is required");
      return;
    }

    const riuscito = await esegui(
      "crea",
      () =>
        api.post("/clinic-events", {
          title: nuovoTitolo.trim(),
          description: nuovaDescrizione.trim(),
        }),
      (evento) => {
        // In testa: l'elenco del server è dal più recente, e la voce appena
        // creata è la più recente di tutte. La POST non porta il numero degli
        // iscritti (lo porta solo l'elenco, Fetta 6): un evento appena nato
        // non ne ha, quindi 0.
        if (evento && evento.id) {
          setEventi((precedenti) => [{ ...evento, participantsCount: 0 }, ...precedenti]);
        }
      }
    );

    if (riuscito) {
      setNuovoTitolo("");
      setNuovaDescrizione("");
    }
  };

  return (
    <HomeShell backTo="/home" width="admin">
      <section className="qpsc-clinic">
        {/* Intestazione */}
        <IntestazionePlancia />

        {erroreCaricamento && <p className="qpsc-clinic__error">{erroreCaricamento}</p>}

        {erroreAzione && (
          <p className="qpsc-clinic__error" role="alert">
            {erroreAzione}
          </p>
        )}

        {/* NUOVO EVENTO — sta in cima perché è il gesto per cui si apre questa
            pagina la prima volta, e perché sotto c'è un elenco che cresce. */}
        <div className="qpsc-clinic__block">
          <div className="qpsc-clinic__block-label">New event</div>

          <label className="qpsc-field">
            <span className="qpsc-field__label">Title</span>
            <input
              type="text"
              className="qpsc-input"
              value={nuovoTitolo}
              onChange={(e) => setNuovoTitolo(e.target.value)}
              placeholder="School holiday clinic"
              maxLength={120}
              disabled={azioneInCorso !== null}
            />
          </label>

          <label className="qpsc-field">
            <span className="qpsc-field__label">Description (optional)</span>
            <textarea
              className="qpsc-input qpsc-clinic__textarea"
              value={nuovaDescrizione}
              onChange={(e) => setNuovaDescrizione(e.target.value)}
              placeholder="What it is, who it is for, where it runs."
              maxLength={2000}
              rows={3}
              disabled={azioneInCorso !== null}
            />
          </label>

          <button
            type="button"
            className="qpsc-tool-btn qpsc-clinic__create"
            onClick={creaEvento}
            disabled={azioneInCorso !== null}
          >
            <FaPlus aria-hidden="true" />
            <span>{azioneInCorso === "crea" ? "Creating…" : "Create event"}</span>
          </button>

          {/* Si dice PRIMA di premere, non dopo: che l'evento nasca aperto è la
              leva del confine, e chi crea deve saperlo in anticipo. */}
          <p className="qpsc-clinic__hint">New events are created open.</p>
        </div>

        {/* ELENCO */}
        <div className="qpsc-clinic__block">
          <div className="qpsc-clinic__list-head">
            <div className="qpsc-clinic__block-label">Events</div>

            {/* L'interruttore dei nascosti (15/9). Forma di casa (`qpsc-check`),
                la stessa di «Show family links» in Team Assignment: nessun
                comando nuovo da imparare. Accenderlo rilegge l'elenco. */}
            <label className="qpsc-check qpsc-clinic__show-hidden">
              <input
                className="qpsc-check__input"
                type="checkbox"
                checked={mostraNascosti}
                onChange={() => setMostraNascosti((attivo) => !attivo)}
                disabled={azioneInCorso !== null}
              />
              <span className="qpsc-check__box" aria-hidden="true" />
              <span>Show hidden events</span>
            </label>
          </div>

          {caricamento && <p className="qpsc-clinic__hint">Loading…</p>}

          {!caricamento && eventi.length === 0 && !erroreCaricamento && (
            <p className="qpsc-clinic__hint">
              {mostraNascosti
                ? "No clinic events yet."
                : "No clinic events in view. Hidden ones, if any, appear with “Show hidden events”."}
            </p>
          )}

          <ul className="qpsc-clinic__list">
            {eventi.map((evento) => {
              const isHidden = evento.hidden === true;
              const gruppi = Array.isArray(evento.groups) ? evento.groups : [];
              // I due pezzi della seconda riga, uniti da « · » come il
              // sottotitolo dei partecipanti: i gruppi solo se ci sono, gli
              // iscritti sempre (anche 0), salvo che il server non sia
              // riuscito a contarli (`null`, Fetta 6).
              const iscritti = evento.participantsCount;
              const meta = [
                gruppi.length > 0
                  ? `${gruppi.length} ${gruppi.length === 1 ? "group" : "groups"}`
                  : null,
                Number.isInteger(iscritti)
                  ? `${iscritti} ${iscritti === 1 ? "participant" : "participants"}`
                  : null,
              ].filter(Boolean);

              return (
                <li
                  key={evento.id}
                  className={
                    isHidden
                      ? "qpsc-clinic__item qpsc-clinic__item--hidden"
                      : "qpsc-clinic__item"
                  }
                >
                  {/* La riga: dalla Fetta 3 porta alla pagina dell'evento
                      (/clinic-events/:id) invece di aprire una tendina. Il
                      pulsante avvolge la riga intera, così è raggiungibile da
                      tastiera come ogni altro comando della pagina. */}
                  <button
                    type="button"
                    className="qpsc-clinic__row"
                    onClick={() => navigate(`/clinic-events/${encodeURIComponent(evento.id)}`)}
                  >
                    <FaChevronRight className="qpsc-clinic__row-caret" aria-hidden="true" />

                    <span className="qpsc-clinic__row-main">
                      <span className="qpsc-clinic__row-title">{evento.title}</span>
                      {/* Niente più date nella riga (Fetta 3): il numero dei
                          gruppi, quando ce ne sono, e dalla Fetta 6 quello
                          degli iscritti. */}
                      {meta.length > 0 && (
                        <span className="qpsc-clinic__row-meta">{meta.join(" · ")}</span>
                      )}
                    </span>

                    {/* Le pastiglie (clinicShared.jsx): lo stato, per cui si
                        guarda l'elenco, e HIDDEN accanto, che compare solo con
                        l'interruttore acceso, perché altrimenti i nascosti
                        non arrivano. */}
                    <PastiglieEvento evento={evento} />
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      </section>
    </HomeShell>
  );
}
